/**
 * Веб-дашборд финансового агента — третья сборка §13.1 ТЗ платформы
 * («три сборки, один шелл»).
 *
 * Своего входа, своих цветов и своего экрана отказа здесь нет: каркас
 * платформы разбирает `401`/`403`, рисует шапку и показывает единый экран
 * отказа §6.4. Сервис передаёт ему одно — откуда брать «кто я».
 *
 * **Роль дашборда — глубина, а не сигналы** (D12). Всё, что помещается в
 * сообщение, живёт в боте; сюда приходят, когда нужно разглядеть: график,
 * таблицу, конкретный чек. Поэтому здесь нет ни сводок, ни подписок —
 * они в боте, и второй их копии быть не должно.
 *
 * **Пять экранов, и раскрытие связывает три из них** (§14.3): «Обзор» →
 * день → «Чеки»; «Продажи» → категория или блюдо → «Чеки». Переход несёт
 * с собой период и фильтр — через адрес строки браузера, а не через
 * состояние в памяти: §14.4 требует, чтобы ссылку можно было переслать.
 */

import { AppShell } from '@shell/AppShell';
import { CopyGuard } from '@shell/CopyGuard';
import { setRestaurantTimeZone } from '@shell/format';
import { Button } from '@shell/ui';
import { financeApi } from './api';
import type { FinanceMe, PeriodParams } from './api';
import { Checks } from './screens/Checks';
import { Control } from './screens/Control';
import { Cost } from './screens/Cost';
import { Menu } from './screens/Menu';
import type { CheckFilters } from './screens/Checks';
import { Data } from './screens/Data';
import { Overview } from './screens/Overview';
import { Sales } from './screens/Sales';
import { Settings } from './screens/Settings';
import { useUrlState } from './useUrlState';

/** Экраны §14.2. Порядок — от общего к частному; «Контроль» после
 *  «Чеков» намеренно: туда приходят, уже увидев цифру, а не вместо неё. */
const SCREENS = [
  { code: 'overview', title: 'Обзор' },
  { code: 'sales', title: 'Продажи' },
  { code: 'cost', title: 'Себестоимость' },
  { code: 'menu', title: 'Меню' },
  { code: 'checks', title: 'Чеки' },
  { code: 'control', title: 'Контроль' },
  { code: 'data', title: 'Данные' },
  { code: 'settings', title: 'Настройки' },
] as const;

function Nav({ current, onChange }: { current: string; onChange: (code: string) => void }) {
  return (
    <nav className="nav" aria-label="Разделы">
      {SCREENS.map((screen) => (
        <Button
          key={screen.code}
          small
          variant={current === screen.code ? 'primary' : 'quiet'}
          onClick={() => onChange(screen.code)}
        >
          {screen.title}
        </Button>
      ))}
    </nav>
  );
}

export function App() {
  const [url, setUrl] = useUrlState();

  const period: PeriodParams = { from: url.from, to: url.to, grain: url.grain };
  const filters: CheckFilters = {
    waiter: url.waiter,
    category: url.category,
    item: url.item,
  };

  const setPeriod = (next: PeriodParams) =>
    setUrl({ from: next.from, to: next.to, grain: next.grain });

  /** Раскрытие §14.3: уносит период с собой и меняет экран. */
  const drill = (next: CheckFilters) =>
    setUrl({
      screen: 'checks',
      // Прежний отбор заменяется, а не дополняется: человек, нажавший на
      // категорию после блюда, спрашивает про категорию, а не про
      // пересечение. Пересечение он получил бы неожиданно и пустым.
      category: next.category,
      item: next.item,
      waiter: next.waiter,
    });

  /** Клик по дню графика — тот же переход, но период сужается до дня. */
  const openDay = (day: string) =>
    setUrl({ screen: 'checks', from: day, to: day, category: undefined, item: undefined });

  return (
    <AppShell title="Тоскана — финансы" fetchMe={financeApi.me}>
      {(me) => {
        const finance = me as FinanceMe;
        // Часовой пояс ресторана приходит тем же ответом: у платформы и у
        // интерфейса не должно быть двух представлений о том, который
        // сейчас час в ресторане (§17.5 ТЗ платформы). Без этого график
        // по часам сдвинул бы вечерний пик на время браузера.
        if (finance.restaurant_tz) {
          setRestaurantTimeZone(finance.restaurant_tz);
        }

        return (
          <>
            <CopyGuard allowed={Boolean(finance.can_copy)} name={finance.full_name ?? null} />
            <Nav current={url.screen} onChange={(screen) => setUrl({ screen })} />

            {url.screen === 'overview' && (
              <Overview period={period} onPeriodChange={setPeriod} onOpenDay={openDay} />
            )}
            {url.screen === 'sales' && (
              <Sales period={period} onPeriodChange={setPeriod} onDrill={drill} />
            )}
            {url.screen === 'cost' && (
              <Cost period={period} onPeriodChange={setPeriod} onDrill={drill} />
            )}
            {url.screen === 'menu' && (
              <Menu period={period} onPeriodChange={setPeriod} onDrill={drill} />
            )}
            {url.screen === 'checks' && (
              <Checks
                period={period}
                filters={filters}
                onPeriodChange={setPeriod}
                onClearFilters={() =>
                  setUrl({ category: undefined, item: undefined, waiter: undefined })
                }
              />
            )}
            {url.screen === 'control' && (
              <Control
                period={period}
                onPeriodChange={setPeriod}
                onOpenSettings={() => setUrl({ screen: 'settings' })}
              />
            )}
            {url.screen === 'data' && <Data />}
            {url.screen === 'settings' && <Settings me={finance} />}
          </>
        );
      }}
    </AppShell>
  );
}
